import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { EndpointsTable } from '../components/status/EndpointsTable'
import { JobsTable } from '../components/status/JobsTable'
import { Stat, StatCard } from '../components/status/StatCard'
import type { Status } from '../components/status/types'
import { formatCount, formatDateTime, formatRelative } from '../lib/format'

function Pass({ pass }: { pass: Record<string, unknown> | null }) {
	if (!pass) return <p className="muted small">Not run yet.</p>
	return (
		<p className="small" style={{ margin: 0 }}>
			{String(pass.date)} · started {formatRelative(String(pass.startedAt))} ·{' '}
			{pass.batches !== undefined
				? `${formatCount(Number(pass.batches))} batches, ${formatCount(Number(pass.apps))} apps`
				: `${formatCount(Number(pass.jobs))} app/country jobs`}
		</p>
	)
}

export function StatusScreen() {
	const status = useQuery({
		queryKey: ['status'],
		queryFn: () => api.get<Status>('/api/v1/status'),
		refetchInterval: 30_000,
	})
	if (status.isPending) return <div className="page-loading">Loading…</div>
	if (status.isError) return <div className="empty">Status is unavailable: {status.error.message}</div>
	const s = status.data
	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<h1>Status</h1>
					<p className="muted small" style={{ margin: 0 }}>
						Updated {formatDateTime(s.generatedAt)} · refreshes every 30 seconds
					</p>
				</div>
			</div>
			<div className="grid">
				<StatCard title="Collector">
					<Stat
						label="Scheduler"
						value={s.scheduler.healthy ? 'running' : 'stopped'}
						tone={s.scheduler.healthy ? 'good' : 'bad'}
					/>
					<Stat label="Last heartbeat" value={formatRelative(s.scheduler.lastHeartbeat)} />
					<Stat label="Charts tracked" value={formatCount(s.charts.enabled)} />
					<Stat label="Polled in 24h" value={formatCount(s.charts.polled24h)} />
					<Stat
						label="Due now / overdue 1h+"
						value={`${formatCount(s.charts.due)} / ${formatCount(s.charts.overdue)}`}
						tone={s.charts.overdue > 1000 ? 'bad' : undefined}
					/>
					<Stat label="Disabled (empty feeds)" value={formatCount(s.charts.disabled)} />
				</StatCard>
				<StatCard title="Daily passes">
					<h3 className="small muted">Ratings</h3>
					<Pass pass={s.passes.ratings} />
					<h3 className="small muted">Reviews</h3>
					<Pass pass={s.passes.reviews} />
				</StatCard>
				<StatCard title="Backup & database">
					<Stat
						label="Last backup"
						value={s.backup ? (s.backup.ok ? formatRelative(s.backup.updatedAt) : 'FAILED') : 'never'}
						tone={s.backup && !s.backup.ok ? 'bad' : undefined}
					/>
					{s.backup?.ok && (
						<Stat
							label="File"
							value={`${s.backup.file} (${((s.backup.sizeBytes ?? 0) / 1e6).toFixed(1)} MB${s.backup.uploaded ? ', in R2' : ''})`}
						/>
					)}
					{s.backup?.error && <p className="error small">{s.backup.error}</p>}
					<Stat label="Database size" value={s.database.size} />
					{s.database.tables.slice(0, 5).map((t) => (
						<Stat key={t.table} label={t.table} value={`${t.size} · ${formatCount(t.rows)} rows`} />
					))}
				</StatCard>
			</div>
			<StatCard title="Apple endpoints">
				{s.endpoints ? (
					<EndpointsTable endpoints={s.endpoints} />
				) : (
					<p className="muted">The collector isn’t running in this process.</p>
				)}
			</StatCard>
			<StatCard title="Jobs">
				<JobsTable jobs={s.jobs} />
			</StatCard>
		</div>
	)
}
